import React, { useState, useRef, useEffect } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { Bot, Send, Sparkles, X, ChevronUp, Zap, MessageSquare, ArrowRight, ShieldCheck, CheckCircle2 } from 'lucide-react';

interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  actionExecuted?: string;
}

export const SolarChatCopilot: React.FC = () => {
  const store = useSolarStore();
  const [isOpen, setIsOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome_1',
      sender: 'assistant',
      text: `Hello! I am your Access Tech AI Solar Copilot 🇳🇬. Our Headquarters is located at 2 Abiriba St, Uzuokoli Road, Umuahia, 440233, Abia State (Phone: 0806 475 2074). Ask me anything about our 1.5kVA, 3.5kVA, or 7.5kVA systems, ₦1,150/L petrol savings, or office directions!`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);

  const quickChips = [
    "What is your office address & phone number?",
    "Can 3.5kVA run my 1.5HP AC & freezer?",
    "Compare Lithium vs Tubular GEL batteries",
    "How much do I save vs ₦1,150/L petrol?",
    "Will solar work during heavy rain in Umuahia?",
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || inputMessage).trim();
    if (!text || loading) return;

    const userMsg: ChatMessage = {
      id: `user_${Date.now()}`,
      sender: 'user',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    setLoading(true);

    try {
      const currentStoreState = {
        selectedPresetId: store.selectedPresetId,
        selectedState: store.selectedState,
        activeLoadWatts: store.activeLoadWatts,
        sunlightIntensity: store.sunlightIntensity,
        gridAvailable: store.gridAvailable,
        calculatedMetrics: store.calculatedMetrics,
      };

      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          currentStoreState,
        }),
      });

      const data = await res.json();
      let actionNotice: string | undefined = undefined;

      // Execute copilot actions on behalf of the user
      if (data.action) {
        const { action, presetId, watts, intensity, consultationType, tab } = data.action;

        if (action === 'SET_PRESET' && presetId) {
          store.setSelectedPreset(presetId);
          actionNotice = `⚡ Updated preset to ${presetId.replace('standard_', '').toUpperCase()}`;
        } else if (action === 'SET_LOAD' && watts) {
          store.setActiveLoad(Number(watts));
          actionNotice = `⚙️ Simulator continuous load set to ${watts}W`;
        } else if (action === 'SET_SUNLIGHT' && intensity !== undefined) {
          store.setSunlight(Number(intensity));
          actionNotice = `☀️ Sunlight intensity adjusted to ${intensity}%`;
        } else if (action === 'TOGGLE_GRID') {
          store.toggleGrid();
          actionNotice = `🔌 Grid (PHCN) status toggled`;
        } else if (action === 'NAVIGATE_TAB' && tab) {
          store.setActiveTab(tab);
          actionNotice = `📑 Navigated to ${tab.toUpperCase()} section`;
        } else if (action === 'OPEN_QUOTE') {
          store.setIsLeadModalOpen(true, consultationType || 'AI Copilot Recommendation');
          actionNotice = `📝 Quote request modal opened`;
        }
      }

      const assistantMsg: ChatMessage = {
        id: `ast_${Date.now()}`,
        sender: 'assistant',
        text: data.reply || "Thank you for reaching out to Access Tech! How else can I assist with your solar engineering quote?",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        actionExecuted: actionNotice,
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      console.error("Chat Error:", err);
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          sender: 'assistant',
          text: "I am currently optimizing our solar physics engine. Our 3.5kVA Pure Sine Wave package is ideal for most 3-4 bedroom apartments!",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      {/* Floating Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="group relative bg-gradient-to-r from-amber-500 via-emerald-500 to-amber-600 hover:from-amber-400 hover:to-emerald-400 text-slate-950 p-4 rounded-full shadow-2xl shadow-emerald-500/40 flex items-center gap-3 transition-all transform hover:scale-105 active:scale-95 border-2 border-amber-300/50"
        >
          <div className="relative">
            <Bot className="w-7 h-7 stroke-[2.5]" />
            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-400 border border-slate-900"></span>
            </span>
          </div>
          <div className="text-left hidden sm:block pr-1">
            <div className="text-xs font-black uppercase tracking-wider leading-none text-slate-950 flex items-center gap-1">
              AI Sales Copilot <Sparkles className="w-3 h-3 text-slate-950" />
            </div>
            <div className="text-[10px] font-bold text-slate-900 leading-tight">Ask questions or edit setup</div>
          </div>
        </button>
      )}

      {/* Expandable Chat Window */}
      {isOpen && (
        <div className="bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl w-[92vw] sm:w-[420px] h-[580px] max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-800 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-amber-500/20 p-2 rounded-xl border border-amber-500/30 text-amber-400">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-black text-slate-100 flex items-center gap-2">
                  Access Tech AI Copilot
                  <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-500/30">
                    ONLINE
                  </span>
                </div>
                <div className="text-[11px] text-slate-400">Trained on Nigerian Fuel & Band A Tariffs</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Prompts Bar */}
          <div className="bg-slate-950/60 p-2.5 border-b border-slate-800/80 overflow-x-auto whitespace-nowrap flex items-center gap-2 no-scrollbar">
            {quickChips.map((chip, idx) => (
              <button
                key={idx}
                onClick={() => handleSendMessage(chip)}
                className="inline-flex items-center gap-1.5 bg-slate-800/90 hover:bg-amber-500/20 hover:border-amber-500/40 text-slate-300 hover:text-amber-300 text-[11px] font-medium px-2.5 py-1 rounded-lg border border-slate-700/60 transition-all shrink-0"
              >
                <Zap className="w-3 h-3 text-amber-400" />
                {chip}
              </button>
            ))}
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-slate-950/40">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl p-3.5 text-xs leading-relaxed ${
                    msg.sender === 'user'
                      ? 'bg-amber-500 text-slate-950 font-medium rounded-br-none shadow-lg shadow-amber-500/10'
                      : 'bg-slate-800/90 border border-slate-700/80 text-slate-200 rounded-bl-none shadow-md'
                  }`}
                >
                  <p className="whitespace-pre-line">{msg.text}</p>

                  {/* Action Execution Notice */}
                  {msg.actionExecuted && (
                    <div className="mt-2.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[11px] font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-emerald-400" />
                      {msg.actionExecuted}
                    </div>
                  )}

                  {/* Call-To-Action buttons inside assistant messages */}
                  {msg.sender === 'assistant' && (
                    <div className="mt-3 pt-2 border-t border-slate-700/50 flex flex-wrap gap-2">
                      <button
                        onClick={() => store.setIsLeadModalOpen(true, 'AI Copilot Direct Quote')}
                        className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 text-[10px] font-bold px-2.5 py-1 rounded-md flex items-center gap-1 transition-all"
                      >
                        Get Official Engineering Quote <ArrowRight className="w-3 h-3" />
                      </button>
                      <a
                        href="https://wa.me/2348064752074?text=Hello%20Access%20Tech%20Solar%2C%20I%20am%20chatting%20with%20your%20AI%20Copilot%20and%20would%20like%20a%20formal%20quote."
                        target="_blank"
                        rel="noreferrer"
                        className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-[10px] font-bold px-2.5 py-1 rounded-md flex items-center gap-1 transition-all"
                      >
                        WhatsApp Sales Rep
                      </a>
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-slate-500 mt-1 px-1">{msg.timestamp}</span>
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-2 bg-slate-800/60 p-3 rounded-xl border border-slate-700/50 w-fit">
                <Bot className="w-4 h-4 text-amber-400 animate-bounce" />
                <span className="text-xs text-slate-400 font-medium">AI Copilot is calculating physics...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Footer */}
          <div className="p-3 bg-slate-900 border-t border-slate-800 flex items-center gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask about 3.5kVA, fuel savings, or set load..."
              className="flex-1 bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 focus:outline-none focus:border-amber-500 placeholder-slate-500"
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={loading || !inputMessage.trim()}
              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 p-2.5 rounded-xl transition-all"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
